/**
 * DashboardPage — neue Default-Home-View nach Login.
 *
 * Eingeführt in Slice-2 Redesign (2026-05-09). Visual-Fidelity-Target:
 * `design-handoff-8-5-2026/v1/pages-core.jsx PageDashboard` Lines 39–180.
 *
 * Layout (top→bottom):
 *  1. Greeting-Block (Datum, tageszeit-abhängiges „Guten Morgen, <Vorname>",
 *     statisches Sub).
 *  2. 4-KPI-Grid (Assets gesamt + Aktive Share-Links + Downloads-30T +
 *     Storage). Tiles 2+3 sind Placeholder bis Backend (Audit-Log + Share-
 *     Tabelle in Sprint B) live ist.
 *  3. Recent-Uploads-Section: SectionHeader + Grid mit max 12 slim Tiles.
 *  4. 2-Column: Activity-Feed (1.3fr) + Expiring-Shares (1fr) — beide als
 *     EmptyState bis Backend live ist.
 *
 * 2 unabhängige Tri-State-Fetches (stats / assets) — eigener
 * AbortController + alive-flag pro Effect, keine Cross-Coupling.
 *
 * Routing: `/dashboard` (zusätzlich, nicht-destruktiv — `/` bleibt Library).
 *
 * Redesign S2 Phase 3b (2026-10-10, `tasks/design-handoff-redesign-s2.md`):
 * neu angeordnet, keine neuen Endpunkte. Reihenfolge: Begrüßung (Paradise
 * Point — einziger Display-Font-Ort) → „Wartet auf Freigabe" + KPI-Spalte →
 * Zuletzt angesehen → Aktivität / ablaufende Links → Collections-Mosaik →
 * Favoriten → Kürzlich hochgeladen. Schnellzugriff „Ohne Tags"/„Meine Uploads"
 * als kompakte Links unter der Begrüßung (die Entwurf-Queue hat jetzt die
 * eigene Karte).
 */
import { useEffect, useState } from 'react';
import { Link } from 'wouter';
import type { AssetStats } from '../../shared/asset-stats-types';
import type { DashboardData } from '../../shared/dashboard-types';
import { ActivityFeedItem } from '../components/ActivityFeedItem';
import { CollectionCardMosaic } from '../components/CollectionCardMosaic';
import { Button } from '../components/ui/Button';
import type { CollectionWithMeta } from '../../shared/collection-types';
import { DashboardAssetRow } from '../components/DashboardAssetRow';
import { DashboardAssetTile } from '../components/DashboardAssetTile';
import { ExpiringShareRow } from '../components/ExpiringShareRow';
import { EmptyState } from '../components/ui/EmptyState';
import { SectionHeader } from '../components/ui/SectionHeader';
import { api } from '../lib/api';
import type { AssetListItem } from '../lib/asset-types';
import {
  getDashboardRecentView,
  setDashboardRecentView,
  type DashboardRecentView,
} from '../lib/dashboard-recent-view';
import { bottomPanelLayout } from '../lib/dashboard-bottom-layout';
import { formatBytes } from '../lib/format-bytes';
import { formatRelative } from '../lib/format-relative';
import { I } from '../lib/icons';
import { useLayoutUser } from '../lib/layout-context';
import { useUploadModal } from '../lib/upload-context';

type StatsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ok'; stats: AssetStats };

type AssetsState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ok'; assets: AssetListItem[] };

type DashboardState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ok'; data: DashboardData };

const RECENT_LIMIT = 12;

/** Tageszeit-abhängiges Greeting. 0–11 = Morgen, 12–17 = Tag, 18–23 = Abend. */
function greetingForHour(hour: number): string {
  if (hour < 12) return 'Guten Morgen';
  if (hour < 18) return 'Guten Tag';
  return 'Guten Abend';
}

// Schnellzugriff-Triage-Karten (2026-06-15): gespeicherte Filter-Views als
// Dashboard-Karten mit Zahlen. Filter („was sehe ich?") gehören NICHT in die
// Sidebar-Nav (Leitprinzip) — hier als kuratierte Arbeits-Queues. `key` ist das
// Count-Feld in DashboardData; `href` die saubere Library-Signatur-URL.
// S2: die Entwurf-Queue hat eine eigene Karte („Wartet auf Freigabe"); die
// übrigen Arbeits-Queues bleiben als kompakte Schnellzugriff-Links.
const QUICK_LINKS = [
  { key: 'untaggedCount', label: 'Ohne Tags', href: '/library?untagged=1' },
  { key: 'myUploadsCount', label: 'Meine Uploads', href: '/library?mine=1' },
] as const satisfies ReadonlyArray<{
  key: 'untaggedCount' | 'myUploadsCount';
  label: string;
  href: string;
}>;

const APPROVAL_THUMBS = 6;
const COLLECTIONS_LIMIT = 4;

export function DashboardPage() {
  const user = useLayoutUser();
  const { open: openUpload } = useUploadModal();

  // Stats (global, unfiltered)
  const [stats, setStats] = useState<StatsState>({ status: 'loading' });
  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();
    setStats({ status: 'loading' });
    (async () => {
      try {
        const data = await api<AssetStats>('/api/assets/stats', { signal: ctrl.signal });
        if (!alive) return;
        setStats({ status: 'ok', stats: data });
      } catch {
        if (!alive) return;
        setStats({ status: 'error' });
      }
    })();
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, []);

  // Recent assets (default-sort = recent, slice 0..12 client-side)
  const [assetsState, setAssetsState] = useState<AssetsState>({ status: 'loading' });
  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();
    setAssetsState({ status: 'loading' });
    (async () => {
      try {
        const data = await api<{ assets: AssetListItem[] }>('/api/assets', { signal: ctrl.signal });
        if (!alive) return;
        setAssetsState({ status: 'ok', assets: data.assets.slice(0, RECENT_LIMIT) });
      } catch {
        if (!alive) return;
        setAssetsState({ status: 'error' });
      }
    })();
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, []);

  // Favoriten-Shelf (Roadmap 3a) — eigener Fetch, bewusst KEIN Tri-State/Error-UI:
  // Shelf-Failure ODER leere Favoriten → leeres Array → Shelf versteckt (silent
  // skip; ein fehlgeschlagener Favoriten-Fetch darf das Dashboard nicht stören).
  const [favorites, setFavorites] = useState<AssetListItem[]>([]);
  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();
    (async () => {
      try {
        const data = await api<{ assets: AssetListItem[] }>('/api/assets/favorites?limit=12', {
          signal: ctrl.signal,
        });
        if (alive) setFavorites(data.assets);
      } catch {
        if (alive) setFavorites([]); // silent skip
      }
    })();
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, []);

  // „Zuletzt angesehen"-Shelf (Roadmap 3b) — eigener Fetch, silent-skip wie
  // Favoriten (Fehler/leer → leeres Array → Shelf versteckt).
  const [recentlyViewed, setRecentlyViewed] = useState<AssetListItem[]>([]);
  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();
    (async () => {
      try {
        const data = await api<{ assets: AssetListItem[] }>(
          '/api/assets/recently-viewed?limit=12',
          { signal: ctrl.signal },
        );
        if (alive) setRecentlyViewed(data.assets);
      } catch {
        if (alive) setRecentlyViewed([]); // silent skip
      }
    })();
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, []);

  // Dashboard real-data (PR-B3 DK-16): consolidated endpoint, replaces 4
  // placeholder-sections (KPI #2 + #3 + Activity-Feed + Expiring-Shares).
  // All-or-nothing — Tri-State error rendert über alle 4 sections gemeinsam.
  // S2: Entwurf-Vorschau für „Wartet auf Freigabe" — bestehender Listen-
  // Endpunkt mit status=draft, nur die ersten Treffer. Fehler → Karte zeigt
  // nur die Zahl (silent skip, wie Favoriten).
  const [drafts, setDrafts] = useState<AssetListItem[]>([]);
  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();
    (async () => {
      try {
        const data = await api<{ assets: AssetListItem[] }>('/api/assets?status=draft', {
          signal: ctrl.signal,
        });
        if (alive) setDrafts(data.assets.slice(0, APPROVAL_THUMBS));
      } catch {
        if (alive) setDrafts([]);
      }
    })();
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, []);

  // S2: Collections-Mosaik (bis 4). Leer/Fehler → Sektion versteckt.
  const [collections, setCollections] = useState<CollectionWithMeta[]>([]);
  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();
    (async () => {
      try {
        const data = await api<{ collections: CollectionWithMeta[] }>('/api/collections', {
          signal: ctrl.signal,
        });
        if (alive) setCollections(data.collections.slice(0, COLLECTIONS_LIMIT));
      } catch {
        if (alive) setCollections([]);
      }
    })();
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, []);

  const [dashboardState, setDashboardState] = useState<DashboardState>({ status: 'loading' });
  useEffect(() => {
    let alive = true;
    const ctrl = new AbortController();
    setDashboardState({ status: 'loading' });
    (async () => {
      try {
        const data = await api<DashboardData>('/api/dashboard/data', { signal: ctrl.signal });
        if (!alive) return;
        setDashboardState({ status: 'ok', data });
      } catch {
        if (!alive) return;
        setDashboardState({ status: 'error' });
      }
    })();
    return () => {
      alive = false;
      ctrl.abort();
    };
  }, []);

  // Datum + tageszeit-Greeting werden EINMAL beim Mount berechnet (lazy
  // initializer). `new Date()` in render-body ist nicht-pure und verletzt
  // react-hooks/purity. Tageszeit-Wechsel mid-session wird in Slice 2
  // bewusst NICHT live aktualisiert (User-Erlebnis stabil bis re-mount).
  // Pattern aus react.md § "useState lazy-initializer Pflicht".
  const [today] = useState(() =>
    new Date().toLocaleDateString('de-DE', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    }),
  );
  const [greeting] = useState(() => greetingForHour(new Date().getHours()));
  const firstName = user?.displayName ? user.displayName.split(' ')[0] : null;

  // Recent-Uploads view-mode (Grid vs List). localStorage-persisted via
  // helper. Lazy-init weil getDashboardRecentView() ist non-pure (touches
  // localStorage). Slice-3 (2026-05-09).
  const [recentView, setRecentView] = useState<DashboardRecentView>(() =>
    getDashboardRecentView(),
  );
  function handleRecentViewChange(v: DashboardRecentView) {
    setRecentView(v);
    setDashboardRecentView(v);
  }

  const draftCount = dashboardState.status === 'ok' ? dashboardState.data.draftCount : null;

  const kpi = (v: number | string | undefined, loading: boolean) =>
    loading ? null : v === undefined ? '—' : typeof v === 'number' ? v.toLocaleString('de-DE') : v;

  return (
    <div className="px-4 md:px-9 py-6 md:py-9 mx-auto max-w-[1320px]">
      {/* Begrüßung — S2: Paradise Point bleibt NUR hier (einziger Display-Font-
          Ort der App); Seitentitel sonst The Sans B (PAGE_H1_CLASS). */}
      <header className="mb-7">
        <div className="text-xs text-ink-500 tabular-nums">{today}</div>
        <h1 className="font-display text-4xl font-bold text-brand-green-700 uppercase mt-1 mb-2">
          {greeting}{firstName ? `, ${firstName}` : ''}.
        </h1>
        <nav aria-label="Schnellzugriff" className="flex flex-wrap items-center gap-2">
          {QUICK_LINKS.map((q) => {
            const count = dashboardState.status === 'ok' ? dashboardState.data[q.key] : null;
            return (
              <Link
                key={q.key}
                href={q.href}
                className="inline-flex items-center gap-2 rounded-control border border-ink-200 bg-white px-3 py-1.5 text-sm text-ink-900 hover:border-ink-300 hover:bg-ink-50 transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-500"
              >
                {q.label}
                <span className="rounded-full bg-ink-100 px-2 text-xs font-semibold tabular-nums text-ink-700">
                  {count ?? '…'}
                </span>
              </Link>
            );
          })}
        </nav>
      </header>

      {/* Freigabe-Queue + KPI-Spalte */}
      <div className="grid grid-cols-1 lg:grid-cols-[2fr_1fr] gap-6 mb-10">
        <section
          aria-labelledby="approval-title"
          className="rounded-2xl border border-ink-200 bg-white p-5 shadow-soft"
        >
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 id="approval-title" className="text-[19px] font-bold text-ink-900">
                Wartet auf Freigabe
              </h2>
              <p className="text-sm text-ink-500 mt-0.5">
                {draftCount === null
                  ? 'Entwürfe werden geladen…'
                  : draftCount === 0
                    ? 'Alles freigegeben — keine offenen Entwürfe.'
                    : `${draftCount.toLocaleString('de-DE')} ${draftCount === 1 ? 'Entwurf' : 'Entwürfe'} zur Prüfung`}
              </p>
            </div>
            {draftCount !== null && draftCount > 0 && (
              <Button asChild variant="primary" size="sm">
                <Link href="/library?status=draft">Prüfen</Link>
              </Button>
            )}
          </div>
          {draftCount !== null && draftCount > 0 && drafts.length > 0 && (
            <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
              {drafts.map((a) => (
                <DashboardAssetTile key={a.id} asset={a} />
              ))}
            </div>
          )}
          {draftCount === 0 && (
            <div className="flex items-center gap-3 rounded-control bg-brand-green-50 px-4 py-3 text-sm text-brand-green-700">
              <I.check size={18} aria-hidden />
              Nichts zu tun. Neue Entwürfe erscheinen hier.
            </div>
          )}
        </section>

        <section aria-label="Kennzahlen" className="grid grid-cols-2 gap-px overflow-hidden rounded-2xl border border-ink-200 bg-ink-100">
          <KpiTile
            label="Assets gesamt"
            value={kpi(stats.status === 'ok' ? stats.stats.total : undefined, stats.status === 'loading')}
            sub={
              stats.status === 'ok' && stats.stats.lastUpload
                ? `Letzter Upload ${formatRelative(stats.stats.lastUpload)}`
                : 'Globaler Stand'
            }
            href="/library"
          />
          <KpiTile
            label="Speicher"
            value={kpi(
              stats.status === 'ok' ? formatBytes(stats.stats.totalBytes) : undefined,
              stats.status === 'loading',
            )}
            sub="Genutzter Speicher"
          />
          <KpiTile
            label="Downloads · 30 T"
            value={kpi(
              dashboardState.status === 'ok' ? dashboardState.data.downloads30d : undefined,
              dashboardState.status === 'loading',
            )}
            sub="Letzte 30 Tage"
            href="/insights"
          />
          <KpiTile
            label="Aktive Share-Links"
            value={kpi(
              dashboardState.status === 'ok' ? dashboardState.data.activeSharesCount : undefined,
              dashboardState.status === 'loading',
            )}
            sub="Nicht widerrufen"
            href="/shares"
          />
        </section>
      </div>

      {/* Zuletzt angesehen (Roadmap 3b) — nur wenn vorhanden; leer/Fehler → versteckt. */}
      {recentlyViewed.length > 0 && (
        <>
          <SectionHeader title="Zuletzt angesehen" sub="Schnell zurück zu deinen letzten Assets" titleSize="base" />
          <div className="mb-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {recentlyViewed.map((a) => (
              <DashboardAssetTile key={a.id} asset={a} />
            ))}
          </div>
        </>
      )}

      {/* Aktivität + ablaufende Links (PR-B3 DK-16). Graziöse Degradation bei
          dünnen Daten: beide leer → Section weg; eine mit Content → full-width;
          beide → 2 Spalten. Loading/Error zeigen beide Spalten. */}
      {(() => {
        if (dashboardState.status === 'ok') {
          const layout = bottomPanelLayout(
            dashboardState.data.activityFeed.length,
            dashboardState.data.expiringShares.length,
          );
          if (layout === 'none') return null;
          const cols =
            layout === 'both' ? 'lg:grid-cols-[1.3fr_1fr]' : 'lg:grid-cols-1';
          return (
            <div className={`grid grid-cols-1 ${cols} gap-6 mb-10`}>
              {(layout === 'both' || layout === 'activity-only') && (
                <ActivityColumn state={dashboardState} />
              )}
              {(layout === 'both' || layout === 'shares-only') && (
                <ExpiringSharesColumn state={dashboardState} />
              )}
            </div>
          );
        }
        return (
          <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-6 mb-10">
            <ActivityColumn state={dashboardState} />
            <ExpiringSharesColumn state={dashboardState} />
          </div>
        );
      })()}

      {/* Collections-Mosaik (S2) — nur wenn vorhanden. */}
      {collections.length > 0 && (
        <>
          <SectionHeader
            title="Collections"
            sub="Zuletzt bearbeitete Sammlungen"
            titleSize="base"
            right={
              <Link
                href="/collections"
                className="text-sm font-medium text-brand-green-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 rounded"
              >
                Alle ansehen →
              </Link>
            }
          />
          <div className="mb-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {collections.map((c) => (
              <CollectionCardMosaic key={c.id} collection={c} />
            ))}
          </div>
        </>
      )}

      {/* Favoriten (Roadmap 3a) — nur wenn vorhanden; leer ODER Fetch-Fehler → versteckt. */}
      {favorites.length > 0 && (
        <>
          <SectionHeader
            title="Favoriten"
            sub="Deine markierten Assets"
            titleSize="base"
            right={
              <Link
                href="/favorites"
                className="text-sm font-medium text-brand-green-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 rounded"
              >
                Alle ansehen →
              </Link>
            }
          />
          <div className="mb-10 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {favorites.map((a) => (
              <DashboardAssetTile key={a.id} asset={a} />
            ))}
          </div>
        </>
      )}

      {/* Kürzlich hochgeladen */}
      <SectionHeader
        title="Kürzlich hochgeladen"
        sub={`Die letzten ${RECENT_LIMIT} Uploads`}
        titleSize="base"
        right={<ViewToggle value={recentView} onChange={handleRecentViewChange} />}
      />
      <div className="mb-10">
        {assetsState.status === 'loading' && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i}>
                <div className="aspect-square rounded-lg bg-ink-100 animate-pulse mb-2" />
                <div className="h-3 w-3/4 rounded bg-ink-100 animate-pulse mb-1" />
                <div className="h-2.5 w-1/2 rounded bg-ink-100 animate-pulse" />
              </div>
            ))}
          </div>
        )}
        {assetsState.status === 'error' && (
          <EmptyState
            icon={<I.close size={20} />}
            title="Recent Uploads konnten nicht geladen werden"
            sub="Verbindungsfehler. Lade die Seite neu."
          />
        )}
        {assetsState.status === 'ok' && assetsState.assets.length === 0 && (
          <EmptyState
            icon={<I.upload size={20} />}
            title="Noch keine Uploads"
            sub="Lade dein erstes Asset hoch und sieh es hier."
            action={
              // S2: grün statt gelb — Gelb gehört dem Hochladen-CTA in der Topbar.
              <Button variant="primary" size="sm" leadingIcon={<I.upload size={14} />} onClick={() => openUpload()}>
                Upload starten
              </Button>
            }
          />
        )}
        {assetsState.status === 'ok' && assetsState.assets.length > 0 && (
          <>
            {recentView === 'grid' ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                {assetsState.assets.map((a) => (
                  <DashboardAssetTile key={a.id} asset={a} />
                ))}
              </div>
            ) : (
              <div className="border border-ink-100 rounded-md bg-white overflow-hidden">
                {assetsState.assets.map((a) => (
                  <DashboardAssetRow key={a.id} asset={a} />
                ))}
              </div>
            )}
            {/* "Alle Uploads →" nur wenn die Liste voll ist (== RECENT_LIMIT). */}
            {assetsState.assets.length === RECENT_LIMIT && (
              <div className="flex justify-end pt-3 mt-3 border-t border-ink-100">
                <Link
                  href="/library?sort=recent"
                  className="text-sm text-brand-green-700 hover:underline transition"
                >
                  Alle Uploads →
                </Link>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/** Aktivität-Feed-Spalte. Eigene Tri-State-Behandlung; die Layout-Entscheidung
 *  (ob/wie breit sie rendert) trifft die Parent via bottomPanelLayout. */
function ActivityColumn({ state }: { state: DashboardState }) {
  return (
    <div>
      <SectionHeader title="Aktivität" sub="Deine letzten Aktionen" titleSize="base" />
      {state.status === 'loading' && (
        <div className="space-y-1">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2">
              <div className="w-10 h-10 rounded bg-ink-100 animate-pulse flex-shrink-0" />
              <div className="flex-1 space-y-1">
                <div className="h-3 w-3/4 rounded bg-ink-100 animate-pulse" />
                <div className="h-2.5 w-1/2 rounded bg-ink-100 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      )}
      {state.status === 'error' && (
        <EmptyState
          icon={<I.close size={20} />}
          title="Aktivität konnte nicht geladen werden"
          sub="Verbindungsfehler. Lade die Seite neu."
        />
      )}
      {state.status === 'ok' && state.data.activityFeed.length > 0 && (
        <div className="border border-ink-100 rounded-md bg-white overflow-hidden divide-y divide-ink-100">
          {state.data.activityFeed.map((item) => (
            <ActivityFeedItem key={item.id} item={item} />
          ))}
        </div>
      )}
    </div>
  );
}

/** Expiring-Share-Links-Spalte. user-scoped (anti-enum). Tri-State analog
 *  ActivityColumn; Layout via bottomPanelLayout in der Parent. */
function ExpiringSharesColumn({ state }: { state: DashboardState }) {
  return (
    <div>
      <SectionHeader title="Share-Links" sub="Bald ablaufend (nächste 7 Tage)" titleSize="base" />
      {state.status === 'loading' && (
        <div className="space-y-1">
          {Array.from({ length: 2 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 px-3 py-2">
              <div className="w-9 h-9 rounded bg-ink-100 animate-pulse flex-shrink-0" />
              <div className="flex-1 space-y-1">
                <div className="h-3 w-2/3 rounded bg-ink-100 animate-pulse" />
                <div className="h-2.5 w-1/2 rounded bg-ink-100 animate-pulse" />
              </div>
            </div>
          ))}
        </div>
      )}
      {state.status === 'error' && (
        <EmptyState
          icon={<I.close size={20} />}
          title="Share-Links konnten nicht geladen werden"
          sub="Verbindungsfehler. Lade die Seite neu."
        />
      )}
      {state.status === 'ok' && state.data.expiringShares.length > 0 && (
        <div className="border border-ink-100 rounded-md bg-white overflow-hidden divide-y divide-ink-100">
          {state.data.expiringShares.map((share) => (
            <ExpiringShareRow key={share.id} share={share} />
          ))}
        </div>
      )}
    </div>
  );
}

/** KPI-Tile — eines der 4 Boxes im Top-Grid. Wenn `placeholder=true`, wird
 *  der Tile als nicht-klickbar gerendert (kein Link, opacity-70). Wenn
 *  `value === null`, wird Skeleton-Span gezeigt (Loading-State). */
function KpiTile({
  label,
  value,
  sub,
  href,
  placeholder = false,
}: {
  label: string;
  value: string | null;
  sub: string;
  href?: string;
  placeholder?: boolean;
}) {
  const inner = (
    <>
      <div className="text-xs font-semibold text-ink-500">{label}</div>
      <div className="text-2xl font-bold text-ink-900 tracking-tight my-1.5 tabular-nums">
        {value === null ? (
          <span className="inline-block w-16 h-7 bg-ink-100 rounded animate-pulse" aria-hidden="true" />
        ) : (
          value
        )}
      </div>
      <div className="text-xs text-ink-500">{sub}</div>
    </>
  );

  // Hover-bg NUR auf der Link-Variante (P1 Affordance 2026-06-10): vorher bekam
  // jede Nicht-Placeholder-Tile den bg-Wechsel — auch die nicht-klickbaren ohne
  // href (z.B. „Downloads · 30T", für die es kein sinnvolles Ziel gibt). Der
  // Hover suggerierte damit fälschlich Interaktivität. Jetzt liest die Affordance
  // das tatsächliche Verhalten: klickbar → Hover, statisch → kein Hover.
  const base = 'bg-white px-5 py-4';

  if (placeholder || !href) {
    return <div className={`${base}${placeholder ? ' opacity-70' : ''}`}>{inner}</div>;
  }
  return (
    <Link href={href} className={`block ${base} hover:bg-brand-green-50/50 transition`}>
      {inner}
    </Link>
  );
}

/** ViewToggle — 2-Segment-Buttons Grid/List für Recent-Uploads-Section. */
function ViewToggle({
  value,
  onChange,
}: {
  value: DashboardRecentView;
  onChange: (v: DashboardRecentView) => void;
}) {
  return (
    <div className="flex gap-0.5 border border-ink-200 rounded-md p-0.5 bg-white">
      <button
        type="button"
        onClick={() => onChange('grid')}
        aria-pressed={value === 'grid'}
        aria-label="Raster-Ansicht"
        title="Raster-Ansicht"
        className={`w-7 h-7 flex items-center justify-center rounded transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 ${
          value === 'grid' ? 'bg-ink-100 text-brand-green-700' : 'text-brand-green-600 hover:text-brand-green-700'
        }`}
      >
        <I.grid size={14} />
      </button>
      <button
        type="button"
        onClick={() => onChange('list')}
        aria-pressed={value === 'list'}
        aria-label="Listen-Ansicht"
        title="Listen-Ansicht"
        className={`w-7 h-7 flex items-center justify-center rounded transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-green-700 ${
          value === 'list' ? 'bg-ink-100 text-brand-green-700' : 'text-brand-green-600 hover:text-brand-green-700'
        }`}
      >
        <I.list size={14} />
      </button>
    </div>
  );
}

